import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud, Check, ArrowRight, Star, X, Phone, MapPin, DollarSign, Home, Shield, AlertCircle, MousePointerClick,
  Eye, CheckCircle, UserPlus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePrefetch } from '@/hooks/usePrefetch';
import axios from 'axios';
import { toast } from 'sonner';
import FreePreviewModal from '@/components/FreePreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Analytics helper
const getSessionId = () => {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

const logAnalyticsEvent = async (action, metadata = {}) => {
  try {
    await axios.post(`${API}/analytics/event`, {
      page: '/roofers-paid',
      action,
      metadata,
      sessionId: getSessionId(),
      userAgent: navigator.userAgent
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Analytics tracking failed:', error);
  }
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Quick State Checker - Simplified
const QuickStateChecker = ({ onResults }) => {
  const [state, setState] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCheck = async () => {
    if (!state) {
      toast.error('Please select a state');
      return;
    }

    // Track preview button click
    logAnalyticsEvent('preview_clicked', { state });

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads/check-state`, { state });
      onResults(response.data);
      
      // Track successful preview
      logAnalyticsEvent('preview_success', { 
        state, 
        leadCount: response.data.count 
      });
      
      toast.success(`Found ${response.data.count?.toLocaleString() || 0} homeowners in ${state}!`);
    } catch (error) {
      // Track preview error
      logAnalyticsEvent('preview_error', { 
        state, 
        error: error.message 
      });
      
      toast.error('Unable to check this state');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 border-2 border-blue-200">
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Select Your State
      </label>
      <select
        value={state}
        onChange={(e) => {
          const selectedState = e.target.value;
          setState(selectedState);
          if (selectedState) {
            logAnalyticsEvent('state_selected', { state: selectedState });
          }
        }}
        className="w-full px-4 py-4 text-base border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white mb-1"
        style={{ minHeight: '44px' }}
      >
        <option value="">Choose your state...</option>
        {US_STATES.map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <p className="text-center text-xs text-slate-500 mb-3 leading-relaxed">
        Used by roofing teams nationwide • No spam • We never resell your info
      </p>
      <Button
        onClick={handleCheck}
        disabled={loading || !state}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600 disabled:opacity-100 disabled:cursor-not-allowed py-7 text-base font-extrabold shadow-xl hover:shadow-2xl transition-all text-white"
        style={{ minHeight: '56px' }}
      >
        {loading ? 'Checking...' : 'Get 100 Free Homeowners →'}
      </Button>
      <p className="text-center text-xs text-slate-500 mt-3">
        No credit card • Instant download • First 100 free
      </p>
    </div>
  );
};

// Sample Data Preview Component - Accurate Dataset Structure
const SampleDataPreview = () => {
  const [activeTab, setActiveTab] = useState('contact');

  const sampleRows = [
    {
      firstName: 'VICKI',
      lastName: 'R***',
      address: '***5 E 71st St',
      city: 'Tulsa',
      state: 'OK',
      zip: '74120',
      phone: '(918) 63***',
      dnc: 'Yes',
      age: '65-69',
      ownRent: 'Home Owner',
      homeValue: '$450K - $499K',
      income: '$100K - $149K',
      creditScore: '750 - 799',
      homeAge: '111 yrs',
      maxWind: '85 mph',
      windDate: '2023-06-18',
      maxHail: '1.50"',
      hailDate: '2024-08-15'
    },
    {
      firstName: 'ROBERT',
      lastName: 'M***',
      address: '***2 S Denver Ave',
      city: 'Tulsa',
      state: 'OK',
      zip: '74119',
      phone: '(918) 58***',
      dnc: 'No',
      age: '45-49',
      ownRent: 'Home Owner',
      homeValue: '$200K - $249K',
      income: '$75K - $99K',
      creditScore: '650 - 699',
      homeAge: '68 yrs',
      maxWind: '92 mph',
      windDate: '2023-06-18',
      maxHail: '2.00"',
      hailDate: '2024-08-15'
    }
  ];

  const tabs = [
    { id: 'contact', label: 'Contact' },
    { id: 'demographics', label: 'Demographics' },
    { id: 'storm', label: 'Storm Data' }
  ];

  return (
    <div className="bg-slate-50 border-2 border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-800 text-white px-4 py-3">
        <p className="text-sm font-bold">Sample List Preview</p>
        <p className="text-xs text-slate-300">Actual export fields from OwnerConnect</p>
      </div>

      {/* Mobile: Tabbed Interface */}
      <div className="block lg:hidden">
        <div className="flex border-b border-slate-300 bg-slate-100">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-3 py-3 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              style={{ minHeight: '44px' }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white p-4">
          {activeTab === 'contact' && (
            <div className="space-y-4">
              {sampleRows.map((row, idx) => (
                <div key={idx} className="border-b border-slate-200 pb-4 last:border-0">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500 font-medium">First Name</p>
                      <p className="text-slate-900">{row.firstName}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Last Name</p>
                      <p className="text-slate-900">{row.lastName}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-500 font-medium">Address</p>
                      <p className="text-slate-900">{row.address}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">City</p>
                      <p className="text-slate-900">{row.city}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">State / ZIP</p>
                      <p className="text-slate-900">{row.state} {row.zip}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Phone</p>
                      <p className="text-slate-900">{row.phone}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">DNC</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.dnc === 'Yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {row.dnc}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'demographics' && (
            <div className="space-y-4">
              {sampleRows.map((row, idx) => (
                <div key={idx} className="border-b border-slate-200 pb-4 last:border-0">
                  <p className="text-xs font-bold text-slate-900 mb-2">{row.firstName} {row.lastName} - {row.city}, {row.state}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500 font-medium">Age</p>
                      <p className="text-slate-900">{row.age}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Own/Rent</p>
                      <p className="text-slate-900">{row.ownRent}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Home Value</p>
                      <p className="text-slate-900">{row.homeValue}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Income</p>
                      <p className="text-slate-900">{row.income}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Credit Score</p>
                      <p className="text-slate-900">{row.creditScore}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Home Age</p>
                      <p className="text-slate-900">{row.homeAge}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'storm' && (
            <div className="space-y-4">
              {sampleRows.map((row, idx) => (
                <div key={idx} className="border-b border-slate-200 pb-4 last:border-0">
                  <p className="text-xs font-bold text-slate-900 mb-2">{row.firstName} {row.lastName} - {row.city}, {row.state}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500 font-medium">Max Wind</p>
                      <p className="text-slate-900 font-semibold">{row.maxWind}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Wind Date</p>
                      <p className="text-slate-900">{row.windDate}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Max Hail</p>
                      <p className="text-slate-900 font-semibold">{row.maxHail}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 font-medium">Hail Date</p>
                      <p className="text-slate-900">{row.hailDate}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Full Grouped Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr className="border-b-2 border-slate-300">
              <th colSpan="4" className="px-3 py-2 text-left font-bold text-blue-600 border-r border-slate-300">
                Contact Information
              </th>
              <th colSpan="6" className="px-3 py-2 text-left font-bold text-green-600 border-r border-slate-300">
                Demographics & Financial
              </th>
              <th colSpan="4" className="px-3 py-2 text-left font-bold text-purple-600">
                Property & Storm Data
              </th>
            </tr>
            <tr className="bg-slate-50 border-b border-slate-300">
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Address</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Phone</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-r border-slate-300">DNC</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Age</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Own/Rent</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Home Value</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Income</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Credit</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-r border-slate-300">Home Age</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Max Wind</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Wind Date</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Max Hail</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Hail Date</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {sampleRows.map((row, idx) => (
              <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-3 text-slate-900">
                  <div className="font-medium">{row.firstName} {row.lastName}</div>
                  <div className="text-slate-500">{row.city}, {row.state} {row.zip}</div>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">{row.address}</td>
                <td className="px-3 py-3 text-slate-700">{row.phone}</td>
                <td className="px-3 py-3 border-r border-slate-200">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    row.dnc === 'Yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {row.dnc}
                  </span>
                </td>
                <td className="px-3 py-3 text-slate-700">{row.age}</td>
                <td className="px-3 py-3 text-slate-700">{row.ownRent}</td>
                <td className="px-3 py-3 text-slate-700">{row.homeValue}</td>
                <td className="px-3 py-3 text-slate-700">{row.income}</td>
                <td className="px-3 py-3 text-slate-700">{row.creditScore}</td>
                <td className="px-3 py-3 text-slate-700 border-r border-slate-200">{row.homeAge}</td>
                <td className="px-3 py-3 text-slate-900 font-semibold">{row.maxWind}</td>
                <td className="px-3 py-3 text-slate-700 text-xs">{row.windDate}</td>
                <td className="px-3 py-3 text-slate-900 font-semibold">{row.maxHail}</td>
                <td className="px-3 py-3 text-slate-700 text-xs">{row.hailDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 px-4 py-3 border-t border-slate-300">
        <p className="text-xs text-slate-700 text-center leading-relaxed">
          <span className="font-semibold">Every download includes contact info + demographics + storm history</span> — ready for call centers and canvassing.
        </p>
        <p className="text-xs text-slate-500 text-center mt-2 italic">
          PII masked for privacy. Real field structure from live OwnerConnect exports.
        </p>
      </div>
    </div>
  );
};

// Comparison Table
const ComparisonTable = () => {
  const comparisons = [
    { feature: 'Who owns the data?', us: 'You do (forever)', them: 'Vendor keeps it' },
    { feature: 'Pricing model', us: 'Buy once, no subscription', them: 'Monthly fees or pay-per-lead' },
    { feature: 'Lead sharing', us: 'Exclusive to you', them: 'Shared with competitors' },
    { feature: 'How you use it', us: 'Call center / door knocking', them: 'Wait for inbound calls' },
    { feature: 'DNC filtering', us: 'Built-in', them: 'Usually not included' },
    { feature: 'Renter filtering', us: 'Excluded automatically', them: 'Mixed in' },
  ];

  return (
    <>
      {/* Mobile: Stacked Cards */}
      <div className="block lg:hidden space-y-4">
        {comparisons.map((row, idx) => (
          <div key={idx} className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
            <h4 className="text-sm font-bold text-slate-900 mb-3">{row.feature}</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-0.5">OwnerConnect</p>
                  <p className="text-sm text-slate-800">{row.us}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-0.5">Angi / Others</p>
                  <p className="text-sm text-slate-600">{row.them}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: Table */}
      <div className="hidden lg:block overflow-hidden rounded-xl border-2 border-slate-300 bg-white">
        <div className="grid grid-cols-3 bg-slate-100 border-b-2 border-slate-300">
          <div className="px-3 py-3"></div>
          <div className="px-3 py-3 text-center">
            <p className="text-sm font-bold text-blue-600">OwnerConnect</p>
          </div>
          <div className="px-3 py-3 text-center">
            <p className="text-sm font-bold text-slate-600">Angi / Others</p>
          </div>
        </div>
        {comparisons.map((row, idx) => (
          <div key={idx} className={`grid grid-cols-3 border-b border-slate-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
            <div className="px-3 py-3 text-xs sm:text-sm font-medium text-slate-700">{row.feature}</div>
            <div className="px-3 py-3 text-center">
              <div className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-green-700">
                <Check className="w-4 h-4" />
                <span>{row.us}</span>
              </div>
            </div>
            <div className="px-3 py-3 text-center">
              <div className="inline-flex items-center gap-1 text-xs sm:text-sm text-slate-500">
                <X className="w-4 h-4 text-red-500" />
                <span>{row.them}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// Sticky Mobile CTA Bar
const StickyMobileCTA = ({ onClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isDismissed || !isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-200 shadow-2xl z-50 lg:hidden animate-slide-up">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <p className="text-xs text-slate-600 mb-1">No credit card • Under 60 seconds</p>
            <Button
              onClick={onClick}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold shadow-xl"
              style={{ minHeight: '52px' }}
            >
              Get 100 Free Homeowners →
            </Button>
          </div>
          <button
            onClick={() => setIsDismissed(true)}
            className="text-slate-400 hover:text-slate-600 p-2"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const RoofersLandingPagePaid = () => {
  const navigate = useNavigate();
  const [zipResults, setZipResults] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const signupPrefetch = usePrefetch(() => import('@/pages/SignupPage'));

  // Load Contentsquare script for this page only
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://t.contentsquare.net/uxa/f03b106939085.js';
    script.async = false;
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Track signup button clicks and show modal
  const handleSignupClick = (source = 'default') => {
    logAnalyticsEvent('cta_clicked', { 
      source,
      hasResults: !!zipResults 
    });
    setIsModalOpen(true);
  };

  const handleModalSuccess = (data) => {
    // Modal will handle redirect
    console.log('Preview generated successfully:', data);
  };

  const faqs = [
    {
      q: "Is this Angi or HomeAdvisor leads?",
      a: "No. OwnerConnect is not an inbound lead marketplace. We provide prospecting lists you download and use for outbound (call centers, door knocking). You're not waiting for homeowners to request quotes — you're proactively reaching them first."
    },
    {
      q: "Do I own the data after I buy it?",
      a: "Yes. Buy once, own forever. No subscriptions, no recurring fees. Download your list and use it however you want — import to your CRM, assign to reps, print for canvassers. It's yours."
    },
    {
      q: "How do you avoid renters?",
      a: "We cross-reference property ownership records to exclude renters and only show verified homeowners. You won't waste time knocking on doors or calling people who can't make buying decisions."
    },
    {
      q: "How do you avoid Do Not Call (DNC) numbers?",
      a: "We scrub all lists against the National Do Not Call Registry. You can filter out DNC numbers with one click when building your list."
    },
    {
      q: "How do wind and hail scores work?",
      a: "We track storm activity (hail size and wind speed) for the last 36 months at each property. High scores = recent storm damage = higher likelihood they need a roof. You can filter by storm severity to prioritize your outreach."
    },
    {
      q: "How fast can I get a list?",
      a: "Under 60 seconds. Select your area, apply filters, preview your count, and download instantly as a CSV."
    },
    {
      q: "Is there a subscription?",
      a: "No. You pay once per list. No monthly fees, no auto-renewals, no lock-in."
    },
    {
      q: "What if I don't care about storm data — I just want retail leads?",
      a: "You can skip storm filters entirely and build lists based on roof age, home value, location, or any other criteria. Storm targeting is optional."
    },
    {
      q: "Who is this NOT for?",
      a: "If you're waiting for inbound calls or expecting shared leads sent to your inbox, this isn't the right fit. OwnerConnect is for teams running active outbound — call centers, canvassing crews, and proactive sales operations."
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Sticky Header */}
      <nav className="bg-white border-b border-slate-200 py-3 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-center sm:justify-start">
          <div className="flex items-center gap-2">
            <Cloud className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
            <span className="text-base sm:text-lg font-bold text-slate-900">OwnerConnect</span>
          </div>
        </div>
      </nav>

      {/* HERO - RADICALLY SIMPLIFIED ABOVE THE FOLD */}
      <div className="bg-gradient-to-br from-blue-50 to-white py-12 sm:py-16 min-h-[85vh] sm:min-h-[75vh] flex items-center">
        <div className="max-w-3xl mx-auto px-6 text-center w-full">
          
          {/* Headline - 48px */}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Download 100 Free Homeowner Leads
          </h1>

          {/* Subheadline - 18px */}
          <p className="text-lg text-slate-700 mb-8 leading-relaxed max-w-2xl mx-auto">
            Built for roofing call centers and canvassing teams. No renters. No DNC numbers. Ready to dial today.
          </p>

          {/* CTA Button - 60px height, full-width mobile */}
          <Button
            size="lg"
            onClick={() => handleSignupClick('hero')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-10 font-extrabold shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto text-lg"
            style={{ minHeight: '60px', fontSize: '18px' }}
            {...signupPrefetch}
          >
            Download My 100 Free Leads →
          </Button>

          {/* Small text below button - 14px */}
          <p className="text-sm text-slate-500 mt-3">
            No credit card • Takes 30 seconds • First 100 free
          </p>

          {/* Trust badge - 12px */}
          <p className="text-xs text-slate-400 mt-2">
            Used by 500+ roofing teams
          </p>
        </div>
      </div>

      {/* WHAT YOU GET - Below the Fold */}
      <div className="py-12 bg-white border-t-2 border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">
            What You Get:
          </h2>
          
          {/* Key Bullets - Centered */}
          <div className="space-y-3 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-base sm:text-lg text-slate-800">No more wasted dials on renters</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-base sm:text-lg text-slate-800">No more DNC violations killing your pipeline</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-base sm:text-lg text-slate-800">Target homes that actually got hit with hail or wind</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-base sm:text-lg text-slate-800">Pay once, own forever—no monthly subscription fees</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-base sm:text-lg text-slate-800">Assign lists to reps in under 60 seconds</span>
            </div>
          </div>
        </div>
      </div>

      {/* SHOW ME SECTION - Proof */}
      <div className="py-10 sm:py-12 bg-white border-t-2 border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 text-center">
            What You'll See Inside OwnerConnect
          </h2>
          <p className="text-center text-slate-600 mb-6 text-sm sm:text-base max-w-2xl mx-auto">
            Every list includes verified homeowner data with phone numbers, storm history, and property details — ready to download and use.
          </p>
          <SampleDataPreview />
          <p className="text-center text-slate-600 mt-6 text-sm sm:text-base max-w-2xl mx-auto">
            Every download includes contact info + demographics + storm history — ready for call centers and canvassing.
          </p>
          <div className="text-center mt-6">
            <Button
              size="lg"
              onClick={() => handleSignupClick('sample-data')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-base font-extrabold shadow-xl hover:shadow-2xl transition-all"
              style={{ minHeight: '56px' }}
              {...signupPrefetch}
            >
              Get 100 Free Homeowners →
            </Button>
            <p className="text-xs text-slate-500 mt-2">No credit card • 60 seconds • First 100 free</p>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS - 3 Steps */}
      <div className="py-10 sm:py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-8">
            How Your Team Uses OwnerConnect
          </h2>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-slate-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg sm:text-xl font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Select Your Target Area</h3>
                <p className="text-sm sm:text-base text-slate-700">
                  Pick any state, county, or ZIP code. Filter by storm damage, roof age, home value, DNC status — whatever matches your outreach strategy.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-slate-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg sm:text-xl font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Apply Filters (Exclude Renters, DNC, etc.)</h3>
                <p className="text-sm sm:text-base text-slate-700">
                  Automatically exclude renters and Do Not Call numbers. Target properties with recent hail or wind damage. Build the exact list you need.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-slate-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg sm:text-xl font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Download & Assign to Your Reps</h3>
                <p className="text-sm sm:text-base text-slate-700">
                  Get your CSV instantly. Import to your CRM, assign to call center reps, or print for door-to-door teams. Start calling or knocking the same day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHY THIS BEATS RANDOM LISTS - Comparison */}
      <div className="py-10 sm:py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-3">
            Why This Beats Buying Random Lists
          </h2>
          <p className="text-center text-slate-600 mb-8 text-sm sm:text-base max-w-2xl mx-auto">
            OwnerConnect is built for outbound roofing teams — not an inbound lead marketplace like Angi or HomeAdvisor.
          </p>
          <ComparisonTable />
          <div className="text-center mt-8">
            <Button
              size="lg"
              onClick={() => handleSignupClick('how-it-works')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-base font-extrabold shadow-xl hover:shadow-2xl transition-all"
              style={{ minHeight: '56px' }}
              {...signupPrefetch}
            >
              Get 100 Free Homeowners →
            </Button>
          </div>
        </div>
      </div>

      {/* TESTIMONIALS - Social Proof */}
      <div className="py-10 sm:py-12 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-2">
            What Roofing Teams Are Saying
          </h2>
          <p className="text-center text-xs text-slate-500 mb-8">
            Results vary by market, but these represent real outcomes from roofing companies using OwnerConnect
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white rounded-xl p-5 shadow-lg border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-slate-700 mb-4 leading-relaxed">
                "<span className="font-bold">We booked 7 jobs off our first list.</span> Paid for itself in week one. Way better than buying shared leads."
              </p>
              <div className="border-t border-slate-200 pt-3 space-y-1">
                <p className="font-bold text-slate-900 text-sm">Mike R.</p>
                <p className="text-xs text-slate-600">Owner, Summit Roofing</p>
                <p className="text-xs text-slate-500">Dallas, TX</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-lg border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-slate-700 mb-4 leading-relaxed">
                "<span className="font-bold">Our call center wasted way less time</span> once we started filtering out renters and DNC numbers upfront."
              </p>
              <div className="border-t border-slate-200 pt-3 space-y-1">
                <p className="font-bold text-slate-900 text-sm">Angela P.</p>
                <p className="text-xs text-slate-600">Operations Manager, Ironclad Exteriors</p>
                <p className="text-xs text-slate-500">Tampa, FL</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-lg border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-slate-700 mb-4 leading-relaxed">
                "<span className="font-bold">Storm data targeting changed everything.</span> We knocked the right doors instead of guessing."
              </p>
              <div className="border-t border-slate-200 pt-3 space-y-1">
                <p className="font-bold text-slate-900 text-sm">Josh D.</p>
                <p className="text-xs text-slate-600">Sales Manager, Apex Roofing</p>
                <p className="text-xs text-slate-500">Colorado Springs, CO</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PRICING WITH CONTEXT + RISK REDUCTION */}
      <div className="py-10 sm:py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-3">
            Most Teams Cover This in 1-2 Jobs
          </h2>
          <p className="text-center text-base sm:text-lg text-slate-600 mb-2">
            Buy once. Own forever. No subscriptions, no pay-per-click bidding wars, no lock-in.
          </p>
          <p className="text-center text-sm text-slate-600 mb-8">
            Every free preview includes your first 100 verified homeowners at no cost. Built for companies running outbound sales (call centers, canvassing teams, or hybrid models).
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-5 sm:p-6 hover:shadow-xl transition-shadow">
              <div className="text-center mb-5">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Starter</h3>
                <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">$1,000</div>
                <div className="text-slate-600 text-sm sm:text-base mb-2">10,000 Homeowners</div>
                <div className="text-xs text-slate-500">10¢ per homeowner</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-blue-900">What this means:</p>
                <p className="text-xs text-blue-800">~200-300 calls/day for a week, or 1,000+ doors for canvassing</p>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Verified homeowner data</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Storm history + property details</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Instant CSV download</span>
                </li>
              </ul>
              <p className="text-xs text-center text-slate-500 italic">You own the data. No monthly fees.</p>
            </div>

            <div className="bg-blue-600 border-4 border-blue-700 rounded-2xl p-5 sm:p-6 relative transform md:scale-105 shadow-2xl">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-amber-400 text-slate-900 px-3 py-1 rounded-full text-xs font-bold">
                MOST POPULAR
              </div>
              <div className="text-center mb-5">
                <h3 className="text-base sm:text-lg font-bold text-white mb-2">Professional</h3>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">$2,500</div>
                <div className="text-blue-100 text-sm sm:text-base mb-2">30,000 Homeowners</div>
                <div className="text-xs text-blue-200">8.3¢ per homeowner</div>
              </div>
              <div className="bg-blue-700 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-white">What this means:</p>
                <p className="text-xs text-blue-100">Full month of call center volume or multi-week canvassing campaign</p>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-white">Everything in Starter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-white">Better cost per homeowner</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-white">Scale across multiple territories</span>
                </li>
              </ul>
              <Button
                className="w-full bg-white text-blue-600 hover:bg-blue-50 font-extrabold py-6 shadow-lg hover:shadow-xl transition-all"
                style={{ minHeight: '56px' }}
                onClick={() => handleSignupClick('pricing')}
              >
                Get 100 Free Homeowners →
              </Button>
              <p className="text-xs text-center text-blue-100 italic mt-3">You own the data. No monthly fees.</p>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-2xl p-5 sm:p-6 hover:shadow-xl transition-shadow">
              <div className="text-center mb-5">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Enterprise</h3>
                <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">$5,000</div>
                <div className="text-slate-600 text-sm sm:text-base mb-2">100,000 Homeowners</div>
                <div className="text-xs text-slate-500">5¢ per homeowner</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-green-900">What this means:</p>
                <p className="text-xs text-green-800">Multi-state expansion or full quarter of high-volume outreach</p>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Everything in Professional</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Best price per homeowner</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-700">Cover entire regions</span>
                </li>
              </ul>
              <p className="text-xs text-center text-slate-500 italic">You own the data. No monthly fees.</p>
            </div>
          </div>

          <div className="text-center mt-8 bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-medium text-green-900 mb-1">Start with a free preview</p>
            <p className="text-xs text-green-800">See your exact homeowner count before buying. No credit card required.</p>
          </div>
        </div>
      </div>

      {/* OBJECTION HANDLING / FAQ */}
      <div className="py-10 sm:py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-3">
            Common Questions
          </h2>
          <p className="text-center text-slate-600 mb-8 text-sm sm:text-base">
            Here's what roofing teams usually ask before trying OwnerConnect
          </p>
          
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  style={{ minHeight: '44px' }}
                >
                  <span className="font-bold text-sm sm:text-base text-slate-900 pr-4">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4 text-sm text-slate-700 leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FINAL CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-10 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
            Stop Wasting Time on Bad Lists
          </h2>
          <p className="text-base sm:text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
            Get your first 100 verified homeowners free. See exactly who's in your target area—in under 60 seconds.
          </p>
          <Button
            size="lg"
            onClick={() => handleSignupClick('final-cta')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-7 sm:py-8 text-lg font-extrabold shadow-2xl hover:shadow-3xl transition-all"
            style={{ minHeight: '56px' }}
            {...signupPrefetch}
          >
            Get 100 Free Homeowners →
          </Button>
          <p className="text-blue-100 mt-4 text-sm">
            No credit card • 60 seconds • First 100 free
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Cloud className="w-5 h-5" />
            <span className="text-base font-bold">OwnerConnect</span>
          </div>
          <p className="text-slate-400 text-xs">
            © 2025 OwnerConnect. Prospecting lists for roofing teams.
          </p>
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <StickyMobileCTA onClick={() => handleSignupClick('sticky-mobile')} />

      {/* Free Preview Modal */}
      <FreePreviewModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default RoofersLandingPagePaid;
